import { useEffect, useState } from "react";
import { Megaphone, Copy, AlertTriangle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";

const RULES = [
  ["Once Per Storm", "Each business (not each employee) can only send one Skybox announcement per storm cycle."],
  ["Business-Linked", "Only the command for your assigned job location will work."],
  ["Professional Use", "Keep announcements relevant to your business — opening hours, sales, available stock, or special events."],
];

const CmdRow = ({ cmd, place }) => {
  const copy = () => {
    navigator.clipboard.writeText(`/${cmd}`).then(() => toast.success(`Copied /${cmd}`)).catch(() => toast.error("Copy failed"));
  };
  return (
    <button onClick={copy} data-testid={`alert-cmd-${cmd}`}
      className="group flex w-full items-center justify-between gap-3 border border-[#2A201A] bg-obsidian/40 px-4 py-2.5 text-left hover:border-teal/50 transition-colors">
      <span className="font-mono text-sm text-teal whitespace-nowrap">/{cmd}</span>
      <span className="flex-1 text-right text-xs text-[#b8b0a4]">{place}</span>
      <Copy className="h-3.5 w-3.5 text-[#5c554c] group-hover:text-teal shrink-0 transition-colors" />
    </button>
  );
};

// Group flat alert rows by category, preserving first-seen order.
const groupAlerts = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    if (!map.has(r.category)) map.set(r.category, []);
    map.get(r.category).push(r);
  });
  return [...map.entries()].map(([category, items]) => ({ category, items }));
};

export const BusinessAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  useEffect(() => { api.get("/biz-alerts").then(({ data }) => setAlerts(data || [])).catch(() => {}); }, []);
  const groups = groupAlerts(alerts);

  return (
    <div data-testid="business-alerts">
      <div className="flex items-center gap-3 mb-4">
        <Megaphone className="h-6 w-6 text-teal" />
        <h2 className="font-display text-3xl sm:text-4xl font-light text-bone">Business Alerts</h2>
      </div>
      <p className="font-body text-[15px] leading-relaxed text-[#c8c0b4] max-w-3xl">
        The Skybox system lets business owners broadcast messages to the entire server — so customers know when you're open,
        what you're selling, or any special announcements.
      </p>

      <div className="mt-8 space-y-4 max-w-3xl">
        <div>
          <p className="font-feature text-xs uppercase tracking-[0.2em] text-teal mb-2">How It Works</p>
          <p className="font-body text-[15px] leading-relaxed text-[#c8c0b4]">
            Each business location has a <span className="text-bone">/command</span> linked to its job name. Type the command
            followed by your message and it broadcasts server-wide.
          </p>
        </div>

        <div className="border-l-2 border-teal bg-surface/40 px-5 py-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-1.5">Example</p>
          <p className="font-mono text-sm text-teal">/ranch2 <span className="text-bone">We are open for business and have milk and eggs in stock!</span></p>
          <p className="mt-2 text-xs text-[#8F877C]">/ranch2 = Downes Ranch (House #2) · everyone on the server sees your message instantly.</p>
        </div>

        <div>
          <p className="font-feature text-xs uppercase tracking-[0.2em] text-teal mb-3">Important Rules</p>
          <div className="space-y-2.5">
            {RULES.map(([t, d]) => (
              <div key={t} className="flex items-start gap-3 border border-[#2A201A] bg-obsidian/40 px-4 py-3">
                <AlertTriangle className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                <span className="text-sm text-[#c8c0b4]"><span className="font-semibold text-bone">{t}</span> — {d}</span>
              </div>
            ))}
            <div className="flex items-start gap-3 border-l-2 border-gold/50 bg-gold/5 px-4 py-3">
              <ImageIcon className="h-4 w-4 text-gold shrink-0 mt-0.5" />
              <span className="text-sm text-[#c9c0b0]"><span className="font-semibold text-bone">Image Display</span> — your business logo from the server files displays alongside your message. If your logo isn't showing, open a ticket!</span>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-10 font-feature text-xs uppercase tracking-[0.25em] text-teal mb-4">Commands by Business Type</p>
      <Accordion type="multiple" className="space-y-2">
        {groups.map((g) => (
          <AccordionItem key={g.category} value={g.category} data-testid={`alert-group-${g.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="border border-[#2A201A] bg-surface/20 px-0">
            <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-surface/40 [&>svg]:text-teal">
              <span className="flex items-center gap-3 font-feature text-xs uppercase tracking-[0.2em] text-bone">
                {g.category}
                <span className="font-mono text-[10px] text-[#5c554c]">{g.items.length}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-3 pt-0">
              <div className="grid gap-2 sm:grid-cols-2">
                {g.items.map((it) => <CmdRow key={it.id || it.cmd} cmd={it.cmd} place={it.place} />)}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <p className="mt-4 font-mono text-[10px] text-[#5c554c]">Tip: click any command to copy it to your clipboard.</p>
    </div>
  );
};
